import { getApiToken } from '../api';

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const IMPERSONATE_KEY = 'monksheets_impersonate_user';

export interface ApiError {
  statusCode: number;
  error: string;
  message: string;
  details?: unknown;
  /**
   * Machine-readable rejection code. The backend's `errorHandler` has
   * serialized `AppError.code` onto the response body since Phase 22, but
   * nothing on this side copied it, so every caller that wanted to tell one
   * refusal from another had to match on `statusCode` or dig through
   * `details`. MKS-208 is the first consumer: the time-entry dialog offers
   * "file a backdated-hours request" only for `PROJECT_MONTH_LOCKED`, and a
   * message-string match there would break the moment the copy is edited.
   */
  code?: string;
}

export class ApiClientError extends Error {
  statusCode: number;
  details?: unknown;
  code?: string;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.statusCode = error.statusCode;
    this.details = error.details;
    this.code = error.code;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if (response.status === 401) {
      // external-board-redirect-login (2026-07-02) — defense-in-depth. This
      // transport is internal-only by contract (every external-reachable
      // endpoint uses the raw-fetch pattern in externalAuth.ts / externalBoard.ts
      // / externalProjects.ts / externalMilestones.ts instead, precisely to
      // avoid this). If a caller on the external surface reaches client.ts
      // anyway (a future regression), do NOT clear the internal auth token or
      // fire the global `auth:expired` navigation — doing so redirects the
      // external viewer to the INTERNAL Google /login page, which is the exact
      // bug this session fixed. The external session (if any) is untouched by
      // this branch; the caller still gets a rejected promise with a 401
      // ApiClientError to handle locally (e.g. an inline error banner).
      const onExternalSurface =
        typeof window !== 'undefined' && window.location.pathname.startsWith('/external/');
      if (!onExternalSurface) {
        localStorage.removeItem('monksheets_auth_token');
        // Notify authContext so it clears user state immediately (no page reload required)
        window.dispatchEvent(new CustomEvent('auth:expired'));
      }
      throw new ApiClientError({ statusCode: 401, error: 'Unauthorized', message: 'Session expired' });
    }
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      error: 'Unknown Error',
      message: response.statusText,
    }));
    throw new ApiClientError(error);
  }
  // 204 No Content (and any other empty-body 2xx) — skip JSON parse.
  // Callers that type the result as `void` will receive `undefined`.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return response.json();
}

/**
 * Auth + impersonation headers without Content-Type.
 * Exported for use by download/upload callers that set their own content type.
 */
export function getBaseAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getApiToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  try {
    const raw = sessionStorage.getItem(IMPERSONATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.id) {
        headers['X-Impersonate-User'] = String(parsed.id);
      }
    }
  } catch { /* ignore */ }
  // Audience hint: tells the backend which session to honor when the same
  // browser holds both an internal Bearer token AND an external session
  // (common during dev/testing). Spoofable on its own — the underlying
  // session still has to exist — but it disambiguates which one the request
  // is "from" so backend filters apply correctly (CMT-06).
  if (typeof window !== 'undefined') {
    headers['X-Audience'] = window.location.pathname.startsWith('/external/')
      ? 'external'
      : 'internal';
  }
  return headers;
}

function getAuthHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...getBaseAuthHeaders() };
}

const DEFAULT_TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Runs `run(signal)` under an AbortController with the given timeout.
 * On AbortError, throws ApiClientError(408) with a `${label}` prefix.
 */
async function withTimeout<T>(
  timeoutMs: number,
  label: string,
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await run(controller.signal);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiClientError({
        statusCode: 408,
        error: 'Request Timeout',
        message: `${label} timed out after ${timeoutMs / 1000}s`,
      });
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function apiFetch<T>(method: string, endpoint: string, data?: unknown): Promise<T> {
  return withTimeout(DEFAULT_TIMEOUT_MS, 'Request', async (signal) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      credentials: 'include',
      headers: getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
      signal,
    });
    return handleResponse<T>(response);
  });
}

export async function apiGet<T>(endpoint: string): Promise<T> {
  return apiFetch<T>('GET', endpoint);
}

export async function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  return apiFetch<T>('POST', endpoint, data);
}

export async function apiPut<T>(endpoint: string, data?: unknown): Promise<T> {
  return apiFetch<T>('PUT', endpoint, data);
}

export async function apiPatch<T>(endpoint: string, data?: unknown): Promise<T> {
  return apiFetch<T>('PATCH', endpoint, data);
}

export async function apiDelete<T>(endpoint: string, data?: unknown): Promise<T> {
  return apiFetch<T>('DELETE', endpoint, data);
}

export async function apiUpload<T>(endpoint: string, file: File): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);

  return withTimeout(UPLOAD_TIMEOUT_MS, 'Upload', async (signal) => {
    // Don't set Content-Type - browser sets it with boundary for multipart
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: getBaseAuthHeaders(),
      body: formData,
      signal,
    });
    return handleResponse<T>(response);
  });
}
